import { forwardRef, Fragment, useEffect, useRef, useState } from 'react';
import { ArrowSmallDownBtn, ArrowSmallLeftBtn, ArrowSmallRightBtn, ArrowSmallUpBtn, GrabberHorizontal, GrabberVertical } from '@sas/react-svg';
import cx from 'classnames';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { useIsRTL } from '../../../locale';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { useDesignTokens } from '../../../theme';
import { getTopWindowAccessible } from '../../../util';
import { postfixTestId } from '../../../util/testId';
import { Icon } from '../../Icon';
import { InvisibleText } from '../../InvisibleText';
import bundle from '../NovaComponents-Splitter-gui-icu.properties';
import '../Splitter.css';
import cn from '../Splitter.css.json';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const useSplitterId = createUseHtmlId('splitter');
const useSplitterSectionId = createUseHtmlId('splitter-section');
export const SplitterSection = /*#__PURE__*/forwardRef(function SplitterSection({
  resizable = true,
  ...props
}, ref) {
  const {
    startDrag
  } = useDeltaDrag({
    onChange({
      deltaX,
      deltaY
    }) {
      let delta = props.orientation === 'vertical' ? deltaY : deltaX;
      if (props.barPos === 'left') {
        delta *= -1;
      }
      return props.onDrag(delta);
    },
    onCommit: props.onDragEnd
  });
  const isRTL = useIsRTL();
  const collapseIcons = {
    horizontal: {
      left: {
        icon: ArrowSmallRightBtn,
        offset: isRTL ? {
          right: '-0.2rem'
        } : {
          left: '-0.2rem'
        },
        closeIcon: ArrowSmallLeftBtn,
        closeOffset: isRTL ? {
          left: '-0.2rem'
        } : {
          right: '-0.2rem'
        }
      },
      right: {
        icon: ArrowSmallLeftBtn,
        offset: isRTL ? {
          left: '-0.2rem'
        } : {
          right: '-0.2rem'
        },
        closeIcon: ArrowSmallRightBtn,
        closeOffset: isRTL ? {
          right: '-0.2rem'
        } : {
          left: '-0.2rem'
        }
      }
    },
    vertical: {
      left: {
        icon: ArrowSmallDownBtn,
        offset: {
          top: '-0.3rem'
        },
        closeIcon: ArrowSmallUpBtn,
        closeOffset: {
          bottom: '-0.5rem'
        }
      },
      right: {
        icon: ArrowSmallUpBtn,
        offset: {
          bottom: '-0.5rem'
        },
        closeIcon: ArrowSmallDownBtn,
        closeOffset: {
          top: '-0.3rem'
        }
      }
    }
  };
  const dt = useDesignTokens();
  const localize = useTextLocalizer();
  let ariaDescription = props.orientation === 'horizontal' ? localize('splitterBar.verticalResize.aria') : localize('splitterBar.horizontalResize.aria');
  ariaDescription += props.collapsible ? ` ${localize('splitterBar.collapsible.aria')}` : '';
  const isCollapsed = props.isCollapsed ?? false;
  const splitterBarDescriptionId = useSplitterId();
  const splitterSectionId = useSplitterSectionId();
  const toggleButtonRef = useRef(null);
  const splitterBar = (props.barPos === 'left' || props.barPos === 'right') && /*#__PURE__*/_jsxs("div", {
    "data-testid": postfixTestId(props.testId, 'splitterBar'),
    className: cx(cn.bar[props.orientation], props.hideSplitterBar && cn.hideSplitterBar, !resizable && cn.resizable),
    role: "separator",
    tabIndex: resizable || props.collapsible || props.fullyCollapsible ? 0 : -1,
    onMouseDown: event => {
      if (!resizable) return;
      // 0 is left mouse button
      if (event.button !== 0) return;
      // If the event comes from this element focus the element so it receives the focus indicator on mouse down when
      // a custom focus indicator is set.
      if (event.target === event.currentTarget) {
        (event?.currentTarget).focus();
      }
      startDrag([event.clientX, event.clientY]);
      const targetElement = event?.target?.tagName;
      // If the mouse down event is on the toggle icon, then we do not want to start the drag event. Hence we return if the event.target is related to an icon
      if (event?.target === toggleButtonRef.current || targetElement === 'path' || targetElement === 'svg' || targetElement === 'use') return;
      props.onDragStart();
      // don't start a real browser drag and drop
      event.preventDefault();
    },
    onKeyDown: event => props.onKeyDown(event),
    "aria-label": props.barAriaLabel,
    "aria-labelledby": props.barAriaLabelledBy,
    "aria-describedby": splitterBarDescriptionId,
    "aria-orientation": props.orientation === 'horizontal' ? 'vertical' : 'horizontal',
    "aria-valuemin": 0,
    "aria-valuemax": 100,
    "aria-valuenow": props.barAriaValueNow ?? 0,
    "aria-controls": splitterSectionId,
    children: [/*#__PURE__*/_jsx(InvisibleText, {
      id: splitterBarDescriptionId,
      children: ariaDescription
    }), resizable && /*#__PURE__*/_jsx(Icon, {
      className: cn.resizeIcon,
      tokens: () => {
        return {
          fg: dt.themeVariant === 'high-contrast' && !props.hideSplitterBar ? dt.tokens['color-text-neutral-inverse'] : dt.tokens['color-text-neutral']
        };
      },
      icon: props.orientation === 'horizontal' ? GrabberVertical : GrabberHorizontal
    }), (props.collapsible || props.fullyCollapsible) && (props.paneIndex === 0 || props.isLastSection) && /*#__PURE__*/_jsx("div", {
      className: cn.collapseIcon[props.orientation],
      style: !!isCollapsed ? collapseIcons[props.orientation][props.barPos].closeOffset : collapseIcons[props.orientation][props.barPos].offset,
      ref: toggleButtonRef
      /*
        We moved the 'onClick' event from the Icon to this div.
        This was done so that the splitter will be toggled when the user clicks on either the 'collapse icon' OR the 'target area around the collapse icon'
       */,
      onClick: event => {
        props.onToggleCollapsed();
        event.stopPropagation();
      },
      children: /*#__PURE__*/_jsx(Icon, {
        icon: !!isCollapsed ? collapseIcons[props.orientation][props.barPos].closeIcon : collapseIcons[props.orientation][props.barPos].icon,
        tokens: () => {
          return {
            fg: dt.tokens['color-text-neutral']
          };
        }
      })
    })]
  });
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [props.barPos === 'left' && splitterBar, /*#__PURE__*/_jsx("section", {
      id: splitterSectionId,
      role: props.sectionLandmarkLabel ? 'region' : undefined,
      ref: ref,
      className: cx(cn.section, props.className),
      style: {
        flexBasis: props.size === undefined ? 'auto' : props.size,
        flexGrow: props.size === undefined ? 1 : 0,
        flexShrink: props.size === undefined ? 1 : 0,
        overflow: props.isDragging ? 'hidden' : 'auto',
        pointerEvents: props.isDragging ? 'none' : 'auto'
      },
      "aria-label": !props.sectionAriaLabelledBy ? props.sectionAriaLabel : undefined,
      "aria-labelledby": props.sectionAriaLabelledBy,
      "aria-describedby": props.sectionAriaDescribedBy,
      "data-landmark-label": props.sectionLandmarkLabel,
      children: props.children
    }), props.barPos === 'right' && splitterBar]
  });
});

/**
 * mostly copied/striped down from `useSliderDrag` in Slider.
 */
function useDeltaDrag(props) {
  const [isDragging, setIsDragging] = useState(false);
  const lastStartRef = useRef([NaN, NaN]);
  useEffect(() => {
    if (!isDragging) return;
    function delta(clientX, clientY) {
      const delta = {
        deltaX: clientX - lastStartRef.current[0],
        deltaY: clientY - lastStartRef.current[1]
      };
      return delta;
    }
    function onDrag(e) {
      if (!props.onChange) return;
      const onChange = props.onChange;
      const moved = onChange(delta(e.clientX, e.clientY));
      // Only update the last start ref when the bar really moved. So when over dragging, the ref last start could stay in the boundry.
      if (moved) lastStartRef.current = [e.clientX, e.clientY];
    }
    function onDragEnd(e) {
      // only respond to left buttons being lifted
      if (e.button !== 0) return;
      const targetElement = e?.target?.tagName;
      const position = delta(e.clientX, e.clientY);

      // NOTE: create a copy to prevent mutation for the onCommit handler
      props.onChange({
        ...position
      });
      props.onCommit(targetElement === 'path' || targetElement === 'svg' ? 'toggle' : 'resize');
      setIsDragging(false);
    }
    function onDragLeave(_e) {
      setIsDragging(false);
      document.removeEventListener('mousemove', onDrag);
      document.removeEventListener('mouseup', onDragEnd);
    }
    document.addEventListener('mousemove', onDrag);
    document.addEventListener('mouseup', onDragEnd);
    if (getTopWindowAccessible()) {
      top.addEventListener('mouseup', onDragLeave);
    }
    return () => {
      document.removeEventListener('mousemove', onDrag);
      document.removeEventListener('mouseup', onDragEnd);
      if (getTopWindowAccessible()) {
        top.removeEventListener('mouseup', onDragLeave);
      }
    };
  }, [props, isDragging]);
  return {
    startDrag(dragStartClientPos) {
      lastStartRef.current = dragStartClientPos;
      setIsDragging(true);
    },
    abortDrag() {
      setIsDragging(false);
    },
    isDragging
  };
}
//# sourceMappingURL=SplitterSection.js.map